"use client";

import { Core } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web/common/localization";
import * as React from "react";
import styled, { useTheme } from "styled-components";

import { IconButton } from "../../IconButton";
import { Chevron } from "../../icons/foundation/Chevron";
import { Close } from "../../icons/foundation/Close";

import { ModalNavigationProps } from "./ModalNavigationProps";

export const ModalNavigationFixed: React.FC<ModalNavigationProps> = ({
  onClose,
  showCloseButton,
  closeButtonVariant,
  onBack,
  onForward,
  primary,
  closeIconProps,
  backIconProps,
  forwardIconProps,
  showDivider,
  hasImage,
  isMobile,
  navigationOpacity,
}) => {
  const theme = useTheme();
  const modalTheme = theme.modals?.[primary ? "primary" : "default"];
  const iconColor = modalTheme?.color ?? Core.icon.default;
  const iconProps = modalTheme?.iconProps ?? {};
  const { strictLocalize } = useStrictLocalization();

  const closeText: string = strictLocalize({
    id: "sqyFFG",
    defaultMessage: "Close",
    description: {
      text: "alt text for screen reader for the close icon button",
    },
  });

  const previousText: string = strictLocalize({
    id: "E8zXjQ",
    defaultMessage: "Previous",
    description: {
      text: "alt text for screen reader for the < icon button",
    },
  });

  const nextText: string = strictLocalize({
    id: "t+MKgo",
    defaultMessage: "Next",
    description: {
      text: "alt text for screen reader for the > icon button",
    },
  });

  return (
    <HeaderWrapper
      $hasImage={hasImage}
      $isMobile={isMobile}
      $navigationOpacity={navigationOpacity}
      $showDivider={showDivider}
    >
      <NavWrapper>
        {onBack && (
          <IconButton
            aria-label={previousText}
            onClick={onBack}
            variant={hasImage && isMobile ? "secondary" : "tertiary"}
          >
            <Chevron
              color={iconColor}
              direction="left"
              {...iconProps}
              {...backIconProps}
            />
          </IconButton>
        )}
        {onForward && (
          <IconButton
            aria-label={nextText}
            onClick={onForward}
            variant={hasImage && isMobile ? "secondary" : "tertiary"}
          >
            <Chevron
              color={iconColor}
              direction="right"
              {...iconProps}
              {...forwardIconProps}
            />
          </IconButton>
        )}
      </NavWrapper>
      {showCloseButton && (
        <IconButton
          aria-label={closeText}
          onClick={onClose}
          variant={
            closeButtonVariant ??
            (hasImage && isMobile ? "secondary" : "tertiary")
          }
        >
          <Close color={iconColor} {...iconProps} {...closeIconProps} />
        </IconButton>
      )}
    </HeaderWrapper>
  );
};

const HeaderWrapper = styled.div<{
  $hasImage: boolean;
  $isMobile: boolean;
  $navigationOpacity: number;
  $showDivider: boolean;
}>`
  background-color: rgba(
    ${parseInt(Core.surface.inverse.substring(1, 3), 16)},
    ${parseInt(Core.surface.inverse.substring(3, 5), 16)},
    ${parseInt(Core.surface.inverse.substring(5, 7), 16)},
    ${({ $navigationOpacity }) => $navigationOpacity}
  );
  border-bottom: 1px solid
    rgba(
      ${parseInt(Core.border.muted.substring(1, 3), 16)},
      ${parseInt(Core.border.muted.substring(3, 5), 16)},
      ${parseInt(Core.border.muted.substring(5, 7), 16)},
      ${({ $navigationOpacity, $showDivider }) =>
        $showDivider ? $navigationOpacity : 0}
    );
  display: flex;
  justify-content: space-between;
  padding: 24px 12px;
  position: ${({ $hasImage, $isMobile }) =>
    $hasImage && $isMobile ? "absolute" : "sticky"};
  top: 0;
  width: 100%;
  z-index: 100;
`;

const NavWrapper = styled.div`
  display: flex;
  gap: 24px;
`;
